import { useCallback, useState } from 'react';
import { loginSuccess } from 'actions/sessionActions';
import { callApi } from 'apiClient';
import { Errors } from 'shared/errors';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { LoginForm } from '../loginForm/LoginForm';
import { toError } from 'shared/util/toError';
import { featuresLoaded } from 'actions/featuresActions';
import { useAsync } from 'hooks';

export enum Panel {
    Username = 'username',
    Password = 'password',
    AuthCode = 'authcode',
}

const getErrorParam = () => {
    const url = new URL(window.location.href);
    return url.searchParams.get('error');
};

export const LoginPage = () => {
    const { t } = useTranslation();
    const { isMounted } = useAsync();

    const errorParam = getErrorParam();
    const [ busy, setBusy ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState(errorParam ? t(errorParam) : '');
    const [ activePanel, setActivePanel ] = useState(Panel.Username);

    const isFirstStep = activePanel === Panel.Username;
    
    const dispatch = useDispatch();

    const handleLogin = useCallback((username: string, password: string, authCode?: string) => {
        setBusy(true);
        setErrorMessage('');
        void (async () => {
            try {
                const result = await callApi('POST', '/api/login', { login: username, password, authCode });
                const { session, features, redirectUrl } = result;
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                }
                else {
                    const { user, permissions } = session;
                    dispatch(loginSuccess({ user, permissions }));
                    dispatch(featuresLoaded(features));
                }
            }
            catch (e) {
                if (!isMounted()) return;
                const err = toError(e);
            
                if (isFirstStep && err.message === Errors.PASSWORD_REQUIRED) {
                    setActivePanel(Panel.Password);
                }
                else {
                    if (err.message === Errors.MISSING_AUTH_CODE) {
                        setActivePanel(Panel.AuthCode);
                        return;
                    }
                    setActivePanel(Panel.Username);
                    setErrorMessage(t([ `LOGIN.ERROR.${err.message}`, `ERROR.${err.message}`, 'SERVER_ERROR' ]));
                }
            }
            finally {
                isMounted() && setBusy(false);
            }
        })();
    }, [ setBusy, setErrorMessage, setActivePanel, dispatch, t, isMounted ]);

    return (
        <div id="login-page">
            <LoginForm
                busy={ busy }
                errorMessage={ errorMessage }
                login={ handleLogin }
                goBack={ () => setActivePanel(Panel.Username) }
                activePanel={ activePanel }
            />
        </div>
    );
};
